import { Dispatch, useCallback, useReducer, useState } from 'react';

import { ClientFragment } from '@/modules/clients/graphql/fragments/generated/client';
import { ActionItemFragment } from '@/modules/tasks/features/actionItems/graphql/fragments/generated/ActionItem';
import { CreateActionItemInput, CreateClientInput } from '@/types/schema';

import { ActionDialogsEnum, InViewDialogState } from '../../types';

export enum DashboardActions {
  ADD_PROJECT = 'AddProject',
  ADD_NEW_TASK = 'AddNewTask',
  DUPLICATE_TASK = 'DuplicateTask',
  CONVERT_TASK_TO_SUBTASK = 'ConvertTaskToSubtask',
  EDIT_TASK = 'EditNewTask',
  DUPLICATE_PROJECT = 'DuplicateProject',
  EDIT_PROJECT = 'EditProject',
  DELETE_PROJECT = 'DeleteProject',
  ON_CLOSE = 'OnClose',
  DELETE_TASK = 'DeleteTask',
  ADD_LIST = 'AddList',
  EDIT_LIST = 'EditList',
  DUPLICATE_LIST = 'DuplicateList',
  DELETE_LIST = 'DeleteList',
  ADD_SECTION = 'AddSection',
  EDIT_SECTION = 'EditSection',
  MOVE_TO_SECTION = 'MoveToSection',
  DELETE_SECTION = 'DeleteSection',
  ADD_CLIENT = 'AddClient',
  EDIT_CLIENT = 'EditClient',
  DELETE_CLIENT = 'DeleteClient',
  ADD_CLIENT_CONTACT = 'AddClientContact',
  EDIT_CLIENT_CONTACT = 'EditClientContact',
  MOVE_TASK_TO_PROJECT = 'MoveTaskToProject',
  MOVE_TASK_TO_LIST = 'MoveTaskToList',
  REMOVE_TASK = 'RemoveTask',
}

type AddListAction = {
  type: DashboardActions.ADD_LIST;
  payload: {
    folderId?: string;
  };
};

type EditListAction = {
  type: DashboardActions.EDIT_LIST;
  payload: {
    fileId: string;
  };
};

type DeleteListAction = {
  type: DashboardActions.DELETE_LIST;
  payload: {
    fileId: string;
  };
};

type DuplicateListAction = {
  type: DashboardActions.DUPLICATE_LIST;
  payload: {
    duplicateFromFileId: string;
    title: string;
  };
};

type AddSectionAction = {
  type: DashboardActions.ADD_SECTION;
};

type EditSectionAction = {
  type: DashboardActions.EDIT_SECTION;
  payload: {
    folderId: string;
  };
};

type DeleteSectionAction = {
  type: DashboardActions.DELETE_SECTION;
  payload: {
    folderId: string;
  };
};

type MoveToSectionAction = {
  type: DashboardActions.MOVE_TO_SECTION;
  payload: {
    fileId: string;
  };
};

type AddProjectAction = {
  type: DashboardActions.ADD_PROJECT;
  payload: {
    actionType: ActionDialogsEnum.CreateProject;
    /**
     * used to pre-fill the clientId when creating a new project from the client page
     */
    clientId?: string;
  };
};

type AddNewTaskAction = {
  type: DashboardActions.ADD_NEW_TASK;
  payload?: {
    task?: CreateActionItemInput;
  };
};

type DuplicateTaskAction = {
  type: DashboardActions.DUPLICATE_TASK;
  payload: {
    task: CreateActionItemInput;
  };
};

type ConvertTaskToSubtaskAction = {
  type: DashboardActions.CONVERT_TASK_TO_SUBTASK;
  payload: {
    task: ActionItemFragment;
  };
};

type EditTaskAction = {
  type: DashboardActions.EDIT_TASK;
  payload: {
    task: ActionItemFragment;
  };
};

type EditProjectAction = {
  type: DashboardActions.EDIT_PROJECT;
  payload: {
    fileId: string;
  };
};

type DuplicateProjectAction = {
  type: DashboardActions.DUPLICATE_PROJECT;
  payload: {
    duplicateFromFileId: string;
    title: string;
  };
};

type DeleteProjectAction = {
  type: DashboardActions.DELETE_PROJECT;
  payload: {
    fileId: string;
  };
};

type DeleteTaskAction = {
  type: DashboardActions.DELETE_TASK;
  payload: {
    task: ActionItemFragment;
  };
};

type AddClientAction = {
  type: DashboardActions.ADD_CLIENT;
  payload?: {
    client?: CreateClientInput;
  };
};

type EditClientAction = {
  type: DashboardActions.EDIT_CLIENT;
  payload?: {
    client?: CreateClientInput;
  };
};

type DeleteClientAction = {
  type: DashboardActions.DELETE_CLIENT;
  payload?: {
    client?: CreateClientInput;
  };
};

type AddClientContactAction = {
  type: DashboardActions.ADD_CLIENT_CONTACT;
  payload: {
    client: ClientFragment;
  };
};

type EditClientContactAction = {
  type: DashboardActions.EDIT_CLIENT_CONTACT;
  payload: {
    client: ClientFragment;
    clientContactId: string;
  };
};

type MoveTaskToListAction = {
  type: DashboardActions.MOVE_TASK_TO_LIST;
  payload: {
    task: ActionItemFragment;
  };
};

type MoveTaskToProjectAction = {
  type: DashboardActions.MOVE_TASK_TO_PROJECT;
  payload: {
    task: ActionItemFragment;
  };
};

type RemoveTaskAction = {
  type: DashboardActions.REMOVE_TASK;
  payload: {
    task: ActionItemFragment;
  };
};

type OnCloseAction = {
  type: DashboardActions.ON_CLOSE;
};

type Action =
  | AddProjectAction
  | AddNewTaskAction
  | DuplicateTaskAction
  | ConvertTaskToSubtaskAction
  | EditTaskAction
  | EditProjectAction
  | DuplicateProjectAction
  | DeleteProjectAction
  | OnCloseAction
  | DeleteTaskAction
  | AddListAction
  | EditListAction
  | DeleteListAction
  | DuplicateListAction
  | AddSectionAction
  | EditSectionAction
  | MoveToSectionAction
  | DeleteSectionAction
  | AddClientAction
  | EditClientAction
  | DeleteClientAction
  | AddClientContactAction
  | EditClientContactAction
  | MoveTaskToListAction
  | MoveTaskToProjectAction
  | RemoveTaskAction;

type State = InViewDialogState | undefined;

const DASHBOARD_INITIAL_STATE: State = undefined;

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    // Project actions
    case DashboardActions.ADD_PROJECT: {
      return {
        dialog: ActionDialogsEnum.CreateProject,
        clientId: action.payload.clientId,
      };
    }
    case DashboardActions.DELETE_PROJECT: {
      return {
        dialog: ActionDialogsEnum.DeleteProject,
        fileId: action.payload.fileId,
      };
    }
    case DashboardActions.DUPLICATE_PROJECT: {
      return {
        dialog: ActionDialogsEnum.DuplicateProject,
        duplicateFromFileId: action.payload.duplicateFromFileId,
        title: action.payload.title,
      };
    }
    case DashboardActions.EDIT_PROJECT: {
      return {
        dialog: ActionDialogsEnum.EditProject,
        fileId: action.payload.fileId,
      };
    }
    // List actions
    case DashboardActions.ADD_LIST: {
      return {
        dialog: ActionDialogsEnum.CreateList,
        folderId: action.payload.folderId,
      };
    }
    case DashboardActions.EDIT_LIST: {
      return {
        dialog: ActionDialogsEnum.EditList,
        fileId: action.payload.fileId,
      };
    }
    case DashboardActions.DELETE_LIST: {
      return {
        dialog: ActionDialogsEnum.DeleteList,
        fileId: action.payload.fileId,
      };
    }
    case DashboardActions.DUPLICATE_LIST: {
      return {
        dialog: ActionDialogsEnum.DuplicateList,
        duplicateFromFileId: action.payload.duplicateFromFileId,
        title: action.payload.title,
      };
    }
    // Section actions
    case DashboardActions.ADD_SECTION: {
      return {
        dialog: ActionDialogsEnum.CreateSection,
      };
    }
    case DashboardActions.EDIT_SECTION: {
      return {
        dialog: ActionDialogsEnum.EditSection,
        folderId: action.payload.folderId,
      };
    }
    case DashboardActions.DELETE_SECTION: {
      return {
        dialog: ActionDialogsEnum.DeleteSection,
        folderId: action.payload.folderId,
      };
    }
    case DashboardActions.MOVE_TO_SECTION: {
      return {
        dialog: ActionDialogsEnum.MoveToSection,
        fileId: action.payload.fileId,
      };
    }
    case DashboardActions.ADD_NEW_TASK: {
      return {
        dialog: ActionDialogsEnum.AddNewTask,
        task: action.payload?.task,
      };
    }
    case DashboardActions.DUPLICATE_TASK: {
      return {
        dialog: ActionDialogsEnum.AddNewTask,
        task: action.payload.task,
      };
    }
    case DashboardActions.CONVERT_TASK_TO_SUBTASK: {
      return {
        dialog: ActionDialogsEnum.ConvertTaskToSubtask,
        task: action.payload.task,
      };
    }
    case DashboardActions.EDIT_TASK: {
      return {
        dialog: ActionDialogsEnum.EditTask,
        task: action.payload.task,
      };
    }
    case DashboardActions.DELETE_TASK: {
      return {
        dialog: ActionDialogsEnum.DeleteTask,
        task: action.payload.task,
      };
    }
    // Client actions
    case DashboardActions.ADD_CLIENT: {
      return {
        dialog: ActionDialogsEnum.AddOrEditClient,
        client: action.payload?.client,
      };
    }
    case DashboardActions.EDIT_CLIENT: {
      return {
        dialog: ActionDialogsEnum.AddOrEditClient,
        client: action.payload?.client,
      };
    }
    case DashboardActions.DELETE_CLIENT: {
      return {
        dialog: ActionDialogsEnum.DeleteClient,
        client: action.payload?.client,
      };
    }
    case DashboardActions.ADD_CLIENT_CONTACT: {
      return {
        dialog: ActionDialogsEnum.AddClientContact,
        client: action.payload.client,
      };
    }
    case DashboardActions.EDIT_CLIENT_CONTACT: {
      return {
        dialog: ActionDialogsEnum.AddClientContact,
        client: action.payload.client,
        clientContactId: action.payload.clientContactId,
      };
    }
    case DashboardActions.MOVE_TASK_TO_LIST: {
      return {
        dialog: ActionDialogsEnum.MoveTaskToList,
        task: action.payload.task,
      };
    }
    case DashboardActions.MOVE_TASK_TO_PROJECT: {
      return {
        dialog: ActionDialogsEnum.MoveTaskToProject,
        task: action.payload.task,
      };
    }
    case DashboardActions.REMOVE_TASK: {
      return {
        dialog: ActionDialogsEnum.RemoveTask,
        task: action.payload.task,
      };
    }
    case DashboardActions.ON_CLOSE: {
      return undefined;
    }
    default:
      return state;
  }
};

interface UseDashboardActionsProps {}

function useDashboardActions(props: UseDashboardActionsProps) {
  const {} = props;
  const [lastActedEntityId, setLastActedEntityId] = useState<string | undefined>();

  const [inViewDialogState, dispatch] = useReducer(reducer, DASHBOARD_INITIAL_STATE);

  const onClose = useCallback(() => {
    dispatch({
      type: DashboardActions.ON_CLOSE,
    });
    setLastActedEntityId(undefined);
  }, [dispatch]);

  return {
    inViewDialogState,
    dispatch,
    onClose,
    setLastActedEntityId,
    lastActedEntityId,
  };
}

export type UseDashboardActionsReturnType = ReturnType<typeof useDashboardActions>;
export type DashboardActionsDispatch = Dispatch<Action>;

export default useDashboardActions;
